import React, { useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import storeApi from '../../../../../services/storeApi';
import DialogShell from '../../components/dialog-shell';
import { FieldLabel } from '../../components/settings-ui';

dayjs.extend(customParseFormat);

const QUICK_OPTIONS = [
  { label: '1 hour', value: 1 },
  { label: '3 hours', value: 3 },
  { label: '12 hours', value: 12 },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'Custom', value: 'custom' },
  { label: 'Always', value: 'forever' },
];

export default function WebsiteCloseModal({
  show,
  onClose,
  storeDetail,
  checkUpdate,
}) {
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeOption, setActiveOption] = useState(null);

  const handleClose = () => {
    setShowCustomDate(false);
    setActiveOption(null);
    onClose();
  };

  const onSelectCloseDate = async (time) => {
    setActiveOption(time);

    let websiteClosedAt = null;
    if (time === 'tomorrow') {
      websiteClosedAt = dayjs()
        .tz(storeDetail.timezoneid)
        .add(1, 'day')
        .startOf('day');
    } else if (time === 'forever') {
      websiteClosedAt = dayjs().add(1000, 'year');
    } else if (time === 'custom') {
      setShowCustomDate((v) => !v);
      return;
    } else {
      websiteClosedAt = dayjs().tz(storeDetail.timezoneid).add(time, 'hour');
    }

    storeDetail.websiteclosedat = websiteClosedAt
      ? dayjs(websiteClosedAt).format('YYYY-MM-DD HH:mm:ss')
      : '';

    await onSubmitWebsiteCloseDate(storeDetail);
  };

  const onChangeCloseWebsiteDate = async (date, key) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    storeDetail[key] = formattedDate;
    setShowCustomDate(false);
    await onSubmitWebsiteCloseDate(storeDetail);
  };

  const onSubmitWebsiteCloseDate = async (storeDetail) => {
    const form = {
      ...storeDetail,
      websiteclosedat:
        dayjs(storeDetail.websiteclosedat)
          .tz(storeDetail.timezoneid, true)
          .format() || '',
      apartment: storeDetail?.apartment || '',
    };

    try {
      setLoading(true);
      const formData = new FormData();
      for (const key of Object.keys(form)) {
        formData.append(key, form[key]);
      }
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await storeApi.upsert(formData, config);
    } catch (error) {
      checkUpdate(false);
    } finally {
      checkUpdate(true);
      setLoading(false);
      setShowCustomDate(false);
      setActiveOption(null);
      onClose();
    }
  };

  return (
    <DialogShell
      show={show}
      onClose={handleClose}
      title="Pause online orders"
      description="Choose how long to pause incoming online orders for this store."
      size="md"
    >
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {QUICK_OPTIONS.map((opt) => {
            const isActive = activeOption === opt.value;
            return (
              <button
                key={opt.label}
                type="button"
                disabled={loading}
                onClick={() => onSelectCloseDate(opt.value)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition',
                  isActive
                    ? 'border-transparent bg-blue-600 text-white shadow-sm dark:bg-sky-500'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800',
                  loading && 'cursor-not-allowed opacity-60',
                ].join(' ')}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {showCustomDate && (
          <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50/40 p-4 dark:border-sky-900/40 dark:bg-sky-950/20">
            <FieldLabel>
              <span className="inline-flex items-center gap-2">
                <CalendarDaysIcon
                  className="h-4 w-4 text-slate-500 dark:text-slate-400"
                  aria-hidden="true"
                />
                Custom date
              </span>
            </FieldLabel>
            <DatePicker
              showTime={{
                use12Hours: true,
                format: 'hh:mm A',
                minuteStep: 15,
              }}
              value={
                storeDetail.websiteclosedat
                  ? dayjs(storeDetail.websiteclosedat, 'YYYY-MM-DD HH:mm A')
                  : null
              }
              format={'MM/DD/YYYY hh:mm A'}
              onChange={(e) =>
                onChangeCloseWebsiteDate(
                  dayjs(e, 'YYYY-MM-DD HH:mm A '),
                  'websiteclosedat',
                )
              }
              style={{ width: '100%' }}
              size="large"
              placeholder="Select reopen date"
            />
          </div>
        )}
      </div>
    </DialogShell>
  );
}
